import type { SiteRagReport } from '../types/siteRag';
import type { ReportTypeId } from './reportTypes';
import {
  financialWithheldReasonLabel,
  resolveFinancialDisplayVerdict,
} from '../lib/reportVerdict';

export const REPORT_SURFACE_ORDER: readonly ReportTypeId[] = [
  'feasibility',
  'environmental',
  'power-availability',
  'water-resource',
  'interconnection',
  'supply-chain-risk',
  'test-fit',
  'executive-summary',
  'public-sentiment',
  'custom-brief',
];

export const DECISION_STACK_REPORT_ORDER: readonly ReportTypeId[] = [
  'feasibility',
  'power-availability',
  'executive-summary',
  'public-sentiment',
];

export type ComparisonMetric = {
  id: string;
  label: string;
  getValue: (report: SiteRagReport) => string;
  isCritical?: (report: SiteRagReport) => boolean;
};

const DATA_UNAVAILABLE = 'Data unavailable';

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
};

const firstLine = (value: string | undefined): string => {
  if (!value) return 'Pending';
  const [line] = value.split('\n').map(part => part.trim()).filter(Boolean);
  return line ?? 'Pending';
};

const getBtmStrategyLabel = (report: SiteRagReport): string => {
  const strategy =
    (report as SiteRagReport & { btmStrategy?: string }).btmStrategy
    ?? report.typedPreferencesAtGen?.['power-availability']?.btmStrategy
    ?? report.typedPreferences?.['power-availability']?.btmStrategy
    ?? 'unspecified';
  return strategy.toUpperCase();
};

/* ── 3.4 / D15 — the hurdle rate, with its PROVENANCE ────────────────────────
 *
 * `financialAnalysis.headline.hurdleRatePct` is resolved by
 * `buildFinancialAnalysis` as `preferences.hurdleRateOverride` → engine WACC.
 * On the overwhelming majority of reports NO override is set, so the "hurdle"
 * IS the WACC — and the IRR gate is then arithmetically the NPV gate: for a
 * conventional cashflow discounted at rate r, NPV ≥ 0 ⟺ IRR ≥ r. Printing the
 * bare word "hurdle" (or worse, "Hurdle / target IRR") invites the reader to
 * believe a target return was applied and cleared, when nothing of the sort
 * happened. (Amber is still reachable — Phase 0 redefined it as the MARGIN to
 * the hurdle, not a second pass/fail — so the fix here is the LABEL only.)
 *
 * CATEGORY-ERROR WARNING — DO NOT "FIX" THIS BY USING `equityHurdlePct`.
 * `FinancialAssumptionsCore.equityHurdlePct` is the return the EQUITY tranche
 * demands, and it belongs against the EQUITY IRR (levered, post-debt-service,
 * post-tax). `headline.irrPct` is the UNLEVERED PROJECT IRR — the return on
 * total capital before any financing split. Comparing the two grades a project
 * against a bar it was never measured on and will fail almost every levered
 * deal that is in fact financeable. The engine does not compute an equity IRR
 * at all today, so there is no correct counterparty for `equityHurdlePct`;
 * until it does, WACC (or an explicit unlevered target override) is the ONLY
 * defensible bar for `irrPct`.
 */

/** Where the displayed hurdle rate came from. */
export type HurdleProvenance = 'target-return-override' | 'cost-of-capital-wacc' | 'data-center-discount-rate' | 'unknown';

export interface ResolvedHurdle {
  /** The percentage the verdict banded IRR against (null when unavailable). */
  ratePct: number | null;
  provenance: HurdleProvenance;
  /** Short label naming the provenance — render this, never a bare "hurdle". */
  label: string;
  /** Long-form note for a section that has room for one. */
  note: string;
}

const HURDLE_OVERRIDE_NOTE =
  'An explicit target return was entered in the report preferences; the IRR test bites independently of the NPV test.';
const HURDLE_WACC_NOTE =
  'No target return was entered, so the bar IS the engine WACC. For a conventional cashflow discounted at that same rate, NPV ≥ 0 ⟺ IRR ≥ WACC — this test therefore restates the NPV test rather than adding a second one; the decision-useful signal is the MARGIN.';

/**
 * Resolve the report's hurdle rate together with its provenance.
 *
 * Preference-first: an explicit `hurdleRateOverride` on the gen-time typed
 * preferences is the unambiguous signal that a target return was applied.
 * Absent that, a hurdle that differs from the stamped WACC still indicates an
 * override (a legacy stamp with no persisted preferences); otherwise the
 * hurdle is the cost of capital.
 */
export function resolveHurdleProvenance(report: SiteRagReport): ResolvedHurdle {
  const headline = report.financialAnalysis?.headline;
  const ratePct = toNumber(headline?.hurdleRatePct);
  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return {
    ratePct, provenance: 'data-center-discount-rate', label: 'declared constant-dollar discount rate',
    note: 'The annual capacity-fee model uses its explicitly saved discount rate; no financing-derived WACC or report-preference override is substituted.',
  };
  const waccPct = toNumber(headline?.waccPct);
  const override = toNumber(
    report.typedPreferencesAtGen?.financial?.hurdleRateOverride
      ?? report.typedPreferences?.financial?.hurdleRateOverride,
  );

  if (ratePct == null) {
    return {
      ratePct: null,
      provenance: 'unknown',
      label: 'not resolved',
      note: 'The engine did not resolve a hurdle rate for this report.',
    };
  }

  const isOverride = (override != null && override > 0)
    || (waccPct != null && Math.abs(ratePct - waccPct) > 1e-9);

  return isOverride
    ? {
      ratePct,
      provenance: 'target-return-override',
      label: 'target return (override)',
      note: HURDLE_OVERRIDE_NOTE,
    }
    : {
      ratePct,
      provenance: 'cost-of-capital-wacc',
      label: 'cost of capital (WACC)',
      note: HURDLE_WACC_NOTE,
    };
}

const sharedMetrics: ComparisonMetric[] = [
  {
    id: 'btm-strategy',
    label: 'BTM Strategy',
    getValue: getBtmStrategyLabel,
  },
  {
    id: 'btm-decision',
    label: 'BTM Decision',
    getValue: r => firstLine(r.btmDecisionSummary ?? r.conclusion?.btmDecisionSummary),
  },
  {
    id: 'top-recommendation',
    label: 'Top Recommendation',
    getValue: r => r.conclusion?.recommendation ?? 'Pending',
  },
];

export const COMPARISON_METRICS_BY_TYPE: Record<ReportTypeId, ComparisonMetric[]> = {
  feasibility: [
    ...sharedMetrics,
    {
      id: 'grid-distance',
      label: 'Grid Distance',
      getValue: r => {
        const distance = toNumber(r.datasets?.grid?.substationDist);
        return distance == null ? DATA_UNAVAILABLE : `${distance} km`;
      },
    },
    {
      id: 'water-stress',
      label: 'Water Stress',
      getValue: r => r.datasets?.climate?.waterStress ?? DATA_UNAVAILABLE,
      isCritical: r => (r.datasets?.climate?.waterStress ?? '').toLowerCase() === 'high',
    },
  ],
  environmental: [
    ...sharedMetrics,
    {
      id: 'wetlands',
      label: 'Wetlands',
      getValue: r => r.datasets?.environmental?.wetlandTypes?.join(', ') ?? DATA_UNAVAILABLE,
    },
    {
      id: 'species',
      label: 'Species',
      getValue: r => r.datasets?.environmental?.species?.join(', ') ?? DATA_UNAVAILABLE,
    },
  ],
  'power-availability': [
    ...sharedMetrics,
    {
      id: 'grid-capacity',
      label: 'Grid Capacity',
      getValue: r => r.datasets?.grid?.capacity ?? DATA_UNAVAILABLE,
    },
    {
      id: 'interconnection',
      label: 'Interconnection Queue',
      getValue: r => firstLine(r.powerAnalysis?.interconnectionQueue),
    },
  ],
  'water-resource': [
    ...sharedMetrics,
    {
      id: 'water-stress',
      label: 'Water Stress',
      getValue: r => r.datasets?.climate?.waterStress ?? DATA_UNAVAILABLE,
      isCritical: r => (r.datasets?.climate?.waterStress ?? '').toLowerCase() === 'high',
    },
    {
      id: 'flood-risk',
      label: 'Flood Risk',
      getValue: r => r.datasets?.land?.floodRisk ?? DATA_UNAVAILABLE,
      isCritical: r => (r.datasets?.land?.floodRisk ?? '').toLowerCase() === 'high',
    },
  ],
  interconnection: [
    ...sharedMetrics,
    {
      id: 'fiber',
      label: 'Fiber Availability',
      getValue: r => firstLine(r.interconnectionAnalysis?.fiberAvailability),
    },
    {
      id: 'carrier-diversity',
      label: 'Carrier Diversity',
      getValue: r => firstLine(r.interconnectionAnalysis?.carrierDiversity),
    },
  ],
  'supply-chain-risk': [
    ...sharedMetrics,
    {
      id: 'supply-chain-risks',
      label: 'Supply Chain Risks',
      getValue: r => String((r.risks ?? []).filter(x => String(x.category) === 'supply-chain').length),
      isCritical: r => (r.risks ?? []).some(
        x => String(x.category) === 'supply-chain'
          && (x as { impactLevel?: string }).impactLevel === 'high',
      ),
    },
    {
      id: 'timeline-risk',
      label: 'Timeline Risk',
      getValue: r => firstLine(r.conclusion?.topRisks?.[0] ?? r.executiveSummary),
    },
  ],
  'test-fit': [
    ...sharedMetrics,
    {
      id: 'layout',
      label: 'Layout Recommendation',
      getValue: r => firstLine(r.testFitAnalysis?.buildingPlacement),
    },
    {
      id: 'setbacks',
      label: 'Setback Compliance',
      getValue: r => firstLine(r.testFitAnalysis?.setbackCompliance),
    },
  ],
  'executive-summary': [
    ...sharedMetrics,
    {
      id: 'btm-viability',
      label: 'BTM Viability',
      getValue: r => firstLine(r.btmViabilityAssessment),
    },
    {
      id: 'critical-risks',
      label: 'Critical Risks',
      getValue: r => String(r.executiveSummaryCriticalRisks?.length ?? 0),
    },
  ],
  'public-sentiment': [
    ...sharedMetrics,
    {
      id: 'sentiment-band',
      label: 'Sentiment Band',
      getValue: r => r.sentimentIndex?.band ?? DATA_UNAVAILABLE,
    },
    {
      id: 'sentiment-score',
      label: 'Sentiment Score',
      getValue: r => (typeof r.sentimentIndex?.score === 'number' ? `${r.sentimentIndex.score}` : DATA_UNAVAILABLE),
    },
  ],
  'custom-brief': [
    ...sharedMetrics,
    {
      id: 'brief-objective',
      label: 'Brief Objective',
      getValue: r => r.customBriefObjective ?? DATA_UNAVAILABLE,
    },
    {
      id: 'answered-questions',
      label: 'Questions Answered',
      getValue: r => String(r.customBriefAnswers?.length ?? 0),
    },
  ],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report comparison metrics.
  // Read from the deterministic report.financialAnalysis stamp; '—' until the
  // engine has computed a verdict.
  financial: [
    ...sharedMetrics,
    {
      // Q12 / D6 — the ONE verdict. This cell used to print the RAW engine band
      // while the cover printed the CAPPED display tier, so a comparison table
      // could disagree with the report it was comparing. It now routes through
      // `resolveFinancialDisplayVerdict` (identical to the body section, cover,
      // rollup and DOCX tint) and, when a cap moved the tier off the model's
      // own band, says so in the cell rather than silently showing one of the
      // two numbers.
      id: 'fin-verdict',
      label: 'Financial Verdict',
      getValue: r => {
        if (!r.financialAnalysis) return DATA_UNAVAILABLE;
        const fv = resolveFinancialDisplayVerdict(r);
        if (fv.withheld) {
          return `Verdict withheld — ${financialWithheldReasonLabel(fv.withheldReason)}`;
        }
        return fv.capped ? `${fv.label} (capped from ${fv.modelLabel})` : fv.label;
      },
      // Critical = the DISPLAYED verdict is Unfinanceable, or the model banded
      // red and a cap could only have made it worse (caps are monotonic-down,
      // so a red model band can never display better than red).
      isCritical: r => Boolean(r.financialAnalysis)
        && resolveFinancialDisplayVerdict(r).tier === 'red',
    },
    {
      id: 'fin-npv',
      label: 'NPV',
      getValue: r => {
        const npv = r.financialAnalysis?.headline.npvUsd;
        return typeof npv === 'number' && Number.isFinite(npv) ? `$${(npv / 1_000_000).toFixed(0)}M` : DATA_UNAVAILABLE;
      },
    },
    {
      id: 'fin-irr',
      label: 'Project IRR',
      getValue: r => {
        const irr = r.financialAnalysis?.headline.irrPct;
        return typeof irr === 'number' && Number.isFinite(irr) ? `${irr.toFixed(1)}%` : DATA_UNAVAILABLE;
      },
    },
    {
      // 3.4 / D15 — the hurdle, with its PROVENANCE stated. See
      // `resolveHurdleProvenance` for why the bare word "hurdle" was a lie by
      // omission on most reports.
      id: 'fin-hurdle',
      label: 'Hurdle rate',
      getValue: r => {
        const h = resolveHurdleProvenance(r);
        if (h.ratePct == null) return DATA_UNAVAILABLE;
        return `${h.ratePct.toFixed(1)}% — ${h.label}`;
      },
    },
  ],
};
